import React, { useEffect, useState } from "react";
import {
  Check,
  Minus,
  ChevronDown,
  ArrowRight,
  Sparkles,
  Zap,
  Users,
  Smartphone,
  Calendar,
  HelpCircle,
  MessageSquare,
} from "lucide-react";
import Navbar from "@/components/stori/Navbar";
import Footer from "@/components/stori/Footer";
import SectionHeading from "@/components/stori/SectionHeading";
import { LeadModalProvider, useLeadModal } from "@/components/stori/LeadModalContext";
import LeadFormModal from "@/components/stori/LeadFormModal";
import Seo from "@/seo/Seo";
import { buildFaqLd } from "@/seo/FAQBlock";
import { SITE_URL } from "@/seo/site.config";
import {
  SOFTWARE_PLANS,
  PLAN_KEYS,
  PLAN_LABELS,
  FEATURE_GROUPS,
  EVENT_PACKAGES,
  EVENT_AUDIENCES,
  EVENT_AUDIENCE_LABELS,
  AI_CREDITS,
  FAQ,
} from "@/config/pricing.config";

// Product / Offer JSON-LD built from SOFTWARE_PLANS config.
const PRICING_PRODUCT_LD = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Stori AI Selfie Cam Platform",
  description:
    "Software-only and event-rental plans for the Stori AI selfie cam — capture branded guest photos, detect influencers, automate marketing follow-up.",
  brand: { "@type": "Brand", name: "Stori" },
  offers: SOFTWARE_PLANS.map((p) => ({
    "@type": "Offer",
    name: p.name,
    description: p.tagline,
    price: p.price.monthly.toFixed(2),
    priceCurrency: "USD",
    url: `${SITE_URL}/pricing#${p.key}`,
    availability: "https://schema.org/InStock",
  })),
};

// ---------------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------------
const Badge = ({ kind = "CREDITS" }) => {
  const styles = {
    CREDITS: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    NEW: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    AI: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  };
  return (
    <span
      className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-[0.18em] border ${styles[kind] || styles.CREDITS}`}
      data-testid={`feature-badge-${kind.toLowerCase()}`}
    >
      {kind}
    </span>
  );
};

const FeatureCheck = ({ value }) => {
  if (value === true) {
    return (
      <span
        aria-label="Included"
        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-stori-cyan/15 text-stori-cyan"
      >
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (value === false) {
    return (
      <span
        aria-label="Not included"
        className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-stori-border text-stori-muted/60"
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
    );
  }
  // string value (e.g., credit count)
  return (
    <span className="font-mono text-sm text-white tabular-nums">{value}</span>
  );
};

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
function PricingHero() {
  return (
    <section
      data-testid="pricing-hero"
      className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 border-b border-stori-border overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-stori-cyan/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute inset-0 grid-noise opacity-30 radial-mask pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          PRICING
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h1
          data-testid="pricing-h1"
          className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white"
        >
          Pricing that pays for itself<br />
          <span className="text-stori-cyan cyan-text-glow">in 24 hours.</span>
        </h1>
        <p className="mt-7 text-lg lg:text-xl text-stori-muted max-w-2xl mx-auto leading-relaxed">
          Software for permanent installations or turnkey rentals for live events —
          built for luxury hospitality, flagship retail, fine dining, and premium activations.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab switcher
// ---------------------------------------------------------------------------
function TabSwitcher({ active, onChange }) {
  const tabs = [
    { key: "software", label: "Software Only" },
    { key: "events", label: "Event Rentals" },
  ];
  return (
    <div className="flex justify-center pt-12 lg:pt-14" data-testid="pricing-tabs">
      <div
        role="tablist"
        aria-label="Pricing options"
        className="relative inline-flex p-1 rounded-full border border-stori-border bg-stori-surface1"
      >
        {tabs.map((t) => {
          const selected = active === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={selected}
              data-testid={`tab-${t.key}`}
              onClick={() => onChange(t.key)}
              className={`relative z-10 px-6 sm:px-8 py-2.5 rounded-full text-sm font-medium transition-colors ${
                selected ? "text-black" : "text-stori-muted hover:text-white"
              }`}
            >
              {selected && (
                <span className="absolute inset-0 -z-10 bg-stori-cyan rounded-full" />
              )}
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Billing toggle (Software Only)
// ---------------------------------------------------------------------------
const BILLING_OPTIONS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "annual", label: "Annually" },
];

function BillingToggle({ cadence, onChange }) {
  return (
    <div className="flex flex-col items-center gap-3 mt-10" data-testid="billing-toggle">
      <div
        role="tablist"
        aria-label="Billing cadence"
        className="relative inline-flex p-1 rounded-full border border-stori-border bg-stori-surface1"
      >
        {BILLING_OPTIONS.map((opt) => {
          const selected = cadence === opt.key;
          return (
            <button
              key={opt.key}
              role="tab"
              aria-selected={selected}
              data-testid={`billing-${opt.key}`}
              onClick={() => onChange(opt.key)}
              className={`relative z-10 px-5 sm:px-7 py-2.5 rounded-full text-sm font-medium transition-colors ${
                selected ? "text-black" : "text-stori-muted hover:text-white"
              }`}
            >
              {selected && (
                <span className="absolute inset-0 -z-10 bg-stori-cyan rounded-full" />
              )}
              {opt.label}
            </button>
          );
        })}
      </div>
      {cadence === "annual" && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-mono uppercase tracking-[0.15em]">
          Save 20%
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan card
// ---------------------------------------------------------------------------
function PlanCard({ plan, cadence }) {
  const { openModal } = useLeadModal();
  const isPrimary = plan.cta.style === "primary";
  const isPopular = plan.popular;

  const rawValue = plan.price[cadence];
  // For the Annually view, show the total annual amount (monthly × 12).
  const value = cadence === "annual" ? rawValue * 12 : rawValue;
  const priceDisplay = `$${Number(value).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}`;

  const suffix =
    cadence === "weekly" ? "/wk" : cadence === "annual" ? "/yr" : "/mo";
  const billingLine =
    cadence === "weekly"
      ? "billed weekly"
      : cadence === "annual"
      ? "billed annually · save 20%"
      : "billed monthly";

  return (
    <article
      data-testid={`plan-card-${plan.key}`}
      className={`relative flex flex-col rounded-2xl border bg-stori-surface1 transition-all duration-300 hover:-translate-y-1 ${
        isPopular
          ? "border-stori-cyan/60 lg:scale-[1.03] shadow-[0_0_60px_rgba(0,229,255,0.15)] bg-gradient-to-b from-stori-cyan/5 to-transparent"
          : "border-stori-border hover:border-stori-cyan/40"
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400 text-black font-mono text-[10px] uppercase tracking-[0.2em] font-semibold">
          <Sparkles className="w-3 h-3" />
          Popular
        </div>
      )}

      <div className="p-7 flex-1 flex flex-col">
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-stori-cyan">
          {plan.name}
        </div>
        <p className="mt-3 text-sm text-stori-muted leading-relaxed min-h-[2.5rem]">
          {plan.tagline}
        </p>

        {/* Price */}
        <div className="mt-7 flex items-end gap-2">
          <span
            data-testid={`plan-price-${plan.key}`}
            className="font-heading text-5xl font-semibold text-white tracking-tight tabular-nums leading-none"
          >
            {priceDisplay}
          </span>
          <span className="font-mono text-xs text-stori-muted mb-1.5">
            {suffix}
          </span>
        </div>
        <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-stori-muted">
          {billingLine}
        </div>

        {/* Specs */}
        <ul className="mt-8 space-y-2.5 text-sm">
          <SpecRow icon={Users} label="Users" value={plan.specs.users} />
          <SpecRow icon={Zap} label="Publish" value={plan.specs.publishLimit} />
          <SpecRow icon={Smartphone} label="Devices" value={plan.specs.devices} />
          <SpecRow icon={Calendar} label="Events" value={plan.specs.events} />
          <SpecRow icon={MessageSquare} label="Support" value={plan.specs.support} />
        </ul>

        {/* Feature flags */}
        <ul className="mt-6 pt-6 border-t border-stori-border space-y-2.5">
          <FeatureFlag enabled={plan.features.socialPublishing} label="Social Publishing" />
          <FeatureFlag enabled={plan.features.aiPhotoExperiences} label="AI Photo Experiences" />
          <FeatureFlag enabled={plan.features.playbooks} label="Playbooks" />
          <FeatureFlag enabled={plan.features.automation} label="Automation" />
        </ul>

        {/* CTA */}
        <button
          type="button"
          onClick={() => openModal(plan.cta.action)}
          data-testid={`plan-cta-${plan.key}`}
          className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-medium transition-all hover:-translate-y-0.5 ${
            isPrimary
              ? "bg-stori-cyan text-black hover:bg-stori-cyanHover"
              : "border border-stori-border text-white hover:border-stori-cyan/60 hover:text-stori-cyan"
          }`}
        >
          {plan.cta.label}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </article>
  );
}

const SpecRow = ({ icon: Icon, label, value }) => (
  <li className="flex items-center justify-between text-sm">
    <span className="flex items-center gap-2 text-stori-muted">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
    <span className="text-white font-medium tabular-nums">{value}</span>
  </li>
);

const FeatureFlag = ({ enabled, label }) => (
  <li className="flex items-center gap-2.5 text-sm">
    <FeatureCheck value={enabled} />
    <span className={enabled ? "text-white" : "text-stori-muted/70 line-through decoration-stori-border"}>
      {label}
    </span>
  </li>
);

// ---------------------------------------------------------------------------
// Comparison table (collapsible groups)
// ---------------------------------------------------------------------------
function ComparisonTable() {
  const [openGroups, setOpenGroups] = useState(
    () => new Set(FEATURE_GROUPS.map((g) => g.name))
  );
  const toggle = (name) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <section
      data-testid="comparison-table"
      className="relative pt-24 lg:pt-32 pb-12 border-t border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="COMPARE"
          title="Compare all features."
          lead="Detailed feature breakdown across all plans."
          align="center"
        />

        <div className="mt-14 lg:mt-16 rounded-2xl border border-stori-border overflow-hidden">
          {/* Sticky plan header */}
          <div className="overflow-x-auto">
            <div className="min-w-[920px]">
              <div className="sticky top-16 z-20 grid grid-cols-[minmax(280px,2fr)_repeat(3,minmax(120px,1fr))] gap-3 px-5 py-4 bg-stori-surface1 border-b border-stori-border backdrop-blur">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-stori-muted">
                  Feature
                </div>
                {PLAN_KEYS.map((k) => (
                  <div
                    key={k}
                    className={`text-center font-mono text-xs uppercase tracking-[0.2em] ${
                      k === "signature" ? "text-stori-cyan" : "text-stori-muted"
                    }`}
                  >
                    {PLAN_LABELS[k]}
                  </div>
                ))}
              </div>

              {FEATURE_GROUPS.map((group) => {
                const open = openGroups.has(group.name);
                return (
                  <div key={group.name} data-testid={`group-${group.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    <button
                      type="button"
                      onClick={() => toggle(group.name)}
                      aria-expanded={open}
                      className="w-full flex items-center justify-between px-5 py-4 bg-stori-surface2/40 border-b border-stori-border text-left hover:bg-stori-surface2/70 transition-colors"
                    >
                      <span className="font-heading text-base font-semibold text-white tracking-tight">
                        {group.name}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-stori-muted transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </button>

                    {open &&
                      group.features.map((f) => (
                        <div
                          key={f.name}
                          className="grid grid-cols-[minmax(280px,2fr)_repeat(3,minmax(120px,1fr))] gap-3 px-5 py-4 border-b border-stori-border last:border-b-0 hover:bg-stori-surface1/40 transition-colors"
                        >
                          <div>
                            <div className="text-sm text-white flex items-center flex-wrap">
                              {f.name}
                              {f.badges?.map((b) => (
                                <Badge key={b} kind={b} />
                              ))}
                            </div>
                            <div className="mt-1 text-xs text-stori-muted leading-relaxed">
                              {f.description}
                            </div>
                          </div>
                          {PLAN_KEYS.map((k) => (
                            <div key={k} className="flex items-center justify-center">
                              <FeatureCheck value={f.included[k]} />
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// AI credits
// ---------------------------------------------------------------------------
function AICreditsSection() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="ai-credits-section"
      className="relative py-24 lg:py-32 border-t border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="POWER YOUR EXPERIENCES"
          title="AI Credits."
          lead="Power generative photo experiences across every Stori plan. Buy in bulk, save more."
          align="center"
        />

        <div className="mt-14 lg:mt-16 grid lg:grid-cols-2 gap-5">
          {/* Pricing card */}
          <div className="rounded-2xl border border-stori-border bg-stori-surface1 p-8 lg:p-10">
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-stori-cyan">
              AI CREDIT PRICING
            </div>
            <div className="mt-5 flex items-end gap-2">
              <span className="font-heading text-6xl font-semibold text-white tracking-tight leading-none">
                {AI_CREDITS.pricePerCredit}
              </span>
              <span className="text-stori-muted text-sm mb-2">per credit</span>
            </div>
            <p className="mt-4 text-sm text-stori-muted leading-relaxed">
              Buy in bulk to unlock tiered pricing — the more you buy, the less you pay per credit.
            </p>

            <ul className="mt-8 space-y-3">
              {AI_CREDITS.bulkTiers.map((t) => (
                <li
                  key={t.range}
                  className="flex items-center justify-between py-3 border-b border-stori-border last:border-b-0"
                >
                  <span className="text-sm text-stori-muted">{t.range}</span>
                  <span className="text-sm font-medium text-white tabular-nums">{t.price}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => openModal("demo_request")}
              data-testid="ai-credits-cta"
              className="mt-8 inline-flex items-center gap-2 border border-stori-border text-white px-5 py-3 rounded-md hover:border-stori-cyan/60 hover:text-stori-cyan transition-all"
            >
              Buy AI Credits
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Model costs */}
          <div className="rounded-2xl border border-stori-border bg-stori-surface1 p-8 lg:p-10">
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-stori-cyan">
              AI MODEL COSTS
            </div>
            <h3 className="mt-3 font-heading text-2xl font-semibold text-white tracking-tight">
              Credits per generation.
            </h3>
            <div className="mt-8 overflow-hidden rounded-lg border border-stori-border">
              <div className="grid grid-cols-[1fr_auto] px-5 py-3 bg-stori-surface2/50 text-stori-muted font-mono text-[11px] uppercase tracking-[0.18em]">
                <span>AI Model</span>
                <span>Credits</span>
              </div>
              {AI_CREDITS.modelCosts.map((m) => (
                <div
                  key={m.model}
                  className="grid grid-cols-[1fr_auto] px-5 py-3.5 border-t border-stori-border items-center"
                >
                  <span className="text-sm text-white">{m.model}</span>
                  <span className="font-mono text-sm text-stori-cyan tabular-nums">{m.credits}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-stori-muted leading-relaxed">
              Each subscription tier includes monthly bonus credits — see the comparison table for details.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Audience toggle (Event Rentals)
// ---------------------------------------------------------------------------
function AudienceToggle({ audience, onChange }) {
  return (
    <div className="flex flex-col items-center gap-3 mt-10" data-testid="audience-toggle">
      <div
        role="tablist"
        aria-label="Event audience"
        className="relative inline-flex p-1 rounded-full border border-stori-border bg-stori-surface1"
      >
        {EVENT_AUDIENCES.map((key) => {
          const selected = audience === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={selected}
              data-testid={`audience-${key}`}
              onClick={() => onChange(key)}
              className={`relative z-10 px-5 sm:px-7 py-2.5 rounded-full text-sm font-medium transition-colors ${
                selected ? "text-black" : "text-stori-muted hover:text-white"
              }`}
            >
              {selected && (
                <span className="absolute inset-0 -z-10 bg-stori-cyan rounded-full" />
              )}
              {EVENT_AUDIENCE_LABELS[key]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event rentals
// ---------------------------------------------------------------------------
function EventRentals() {
  const { openModal } = useLeadModal();
  const [audience, setAudience] = useState("private");
  return (
    <section
      data-testid="event-rentals-section"
      className="relative pt-12 pb-24 lg:pb-32"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <AudienceToggle audience={audience} onChange={setAudience} />

        <div className="mt-14 lg:mt-16 grid md:grid-cols-3 gap-5">
          {EVENT_PACKAGES.map((pkg) => {
            const isPopular = pkg.popular;
            const isPrimary = pkg.cta.style === "primary";
            const price = pkg.prices[audience];
            return (
              <article
                key={pkg.key}
                data-testid={`event-package-${pkg.key}`}
                className={`relative flex flex-col rounded-2xl border bg-stori-surface1 transition-all duration-300 hover:-translate-y-1 ${
                  isPopular
                    ? "border-stori-cyan/60 lg:scale-[1.03] shadow-[0_0_60px_rgba(0,229,255,0.15)] bg-gradient-to-b from-stori-cyan/5 to-transparent"
                    : "border-stori-border hover:border-stori-cyan/40"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400 text-black font-mono text-[10px] uppercase tracking-[0.2em] font-semibold">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </div>
                )}
                <div className="p-7 flex-1 flex flex-col">
                  <div className="font-mono text-xs uppercase tracking-[0.22em] text-stori-cyan">
                    {pkg.name}
                  </div>
                  <p className="mt-3 text-sm text-stori-muted leading-relaxed min-h-[2.5rem]">
                    {pkg.tagline}
                  </p>

                  <div className="mt-7">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stori-muted">
                      Starting at
                    </div>
                    <div
                      data-testid={`event-package-price-${pkg.key}`}
                      className="mt-1 font-heading text-5xl font-semibold text-white tracking-tight leading-none"
                    >
                      {price}
                    </div>
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-stori-muted text-sm">
                    <Calendar className="w-3.5 h-3.5" />
                    {pkg.duration}
                  </div>

                  <div className="mt-7 pt-6 border-t border-stori-border flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stori-muted mb-4">
                      Includes
                    </div>
                    <ul className="space-y-3">
                      {pkg.includes.map((it) => (
                        <li key={it} className="flex items-start gap-2.5 text-sm text-white">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-stori-cyan flex-shrink-0" />
                          <span className="leading-relaxed">{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={() => openModal(pkg.cta.action)}
                    data-testid={`event-package-cta-${pkg.key}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-medium transition-all hover:-translate-y-0.5 ${
                      isPrimary
                        ? "bg-stori-cyan text-black hover:bg-stori-cyanHover"
                        : "border border-stori-border text-white hover:border-stori-cyan/60 hover:text-stori-cyan"
                    }`}
                  >
                    {pkg.cta.label}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <p className="text-stori-muted text-sm">
            Looking for a permanent installation? Stori also installs in hotels, restaurants, retail, and corporate spaces.{" "}
            <button
              type="button"
              onClick={() => openModal("demo_request")}
              data-testid="permanent-install-cta"
              className="text-stori-cyan hover:underline font-medium inline-flex items-center gap-1"
            >
              Talk to our team
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------
function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      data-testid="pricing-faq"
      className="relative py-24 lg:py-32 border-t border-stori-border"
    >
      <div className="max-w-[900px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="QUESTIONS"
          title="Frequently asked."
          align="center"
        />

        <div className="mt-12 lg:mt-14 space-y-3">
          {FAQ.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={i}
                className={`rounded-xl border bg-stori-surface1 transition-colors ${
                  open ? "border-stori-cyan/40" : "border-stori-border"
                }`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  className="w-full flex items-start justify-between gap-6 p-6 text-left"
                >
                  <span className="font-heading text-base lg:text-lg font-semibold text-white tracking-tight">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-stori-muted flex-shrink-0 mt-1 transition-transform ${
                      open ? "rotate-180 text-stori-cyan" : ""
                    }`}
                  />
                </button>
                {open && (
                  <div className="px-6 pb-6 text-sm text-stori-muted leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Footer CTA strip
// ---------------------------------------------------------------------------
function FooterCTA() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="pricing-footer-cta"
      className="relative py-16 lg:py-20 border-t border-stori-border bg-stori-surface1"
    >
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div className="flex items-center gap-4">
          <div className="hidden md:flex w-12 h-12 rounded-full border border-stori-cyan/40 text-stori-cyan items-center justify-center">
            <HelpCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading text-2xl font-semibold text-white tracking-tight">
              Not sure which plan is right?
            </h3>
            <p className="mt-1 text-sm text-stori-muted">
              Talk to our team — we'll scope your space, event, or installation in 20 minutes.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => openModal("demo_request")}
          data-testid="footer-cta-button"
          className="inline-flex items-center gap-2 bg-stori-cyan text-black font-medium px-6 py-3.5 rounded-md hover:bg-stori-cyanHover transition-all hover:-translate-y-0.5"
        >
          Talk to our team
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page composition
// ---------------------------------------------------------------------------
function PricingPageInner() {
  const initialTab = () => {
    if (typeof window === "undefined") return "software";
    const h = window.location.hash.replace("#", "");
    return h === "events" ? "events" : "software";
  };
  const [activeTab, setActiveTab] = useState(initialTab);
  const [cadence, setCadence] = useState("monthly");

  // Sync hash on tab change
  useEffect(() => {
    if (typeof window === "undefined") return;
    const target = `#${activeTab}`;
    if (window.location.hash !== target) {
      window.history.replaceState(null, "", target);
    }
  }, [activeTab]);

  // Listen for back/forward
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace("#", "");
      setActiveTab(h === "events" ? "events" : "software");
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  return (
    <main data-testid="pricing-page" className="bg-stori-bg text-white overflow-x-hidden">
      <Navbar />
      <PricingHero />

      <TabSwitcher active={activeTab} onChange={setActiveTab} />

      {activeTab === "software" ? (
        <>
          <section data-testid="software-tab-content" className="pt-2 pb-12">
            <BillingToggle cadence={cadence} onChange={setCadence} />
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 mt-14 lg:mt-16">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-[1100px] mx-auto">
                {SOFTWARE_PLANS.map((p) => (
                  <PlanCard key={p.key} plan={p} cadence={cadence} />
                ))}
              </div>
            </div>
          </section>
          <ComparisonTable />
          <AICreditsSection />
        </>
      ) : (
        <section data-testid="events-tab-content">
          <EventRentals />
        </section>
      )}

      <FAQSection />
      <FooterCTA />
      <Footer />
      <LeadFormModal />
    </main>
  );
}

export default function Pricing() {
  return (
    <LeadModalProvider>
      <Seo path="/pricing" jsonLd={[PRICING_PRODUCT_LD, buildFaqLd(FAQ)]} />
      <PricingPageInner />
    </LeadModalProvider>
  );
}
